import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCondition,
  AlertPriority,
  AlertCategory,
  AlertChannels,
  AlertQuery,
} from "../types/alerts";
import {
  ALERT_CONDITION_OPTIONS,
  ALERT_PRIORITY_OPTIONS,
  ALERT_TEMPLATES,
  PRIORITY_DESCRIPTIONS,
  formatCategoryLabel,
  isPredefinedCategory,
  PREDEFINED_CATEGORIES,
} from "../constants/alerts";
import { useCreateAlert, useBuildQuery, useTestQuery } from "@/hooks/useAlerts";
import type { TestQueryResponse } from "@/services/alertsService";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Eye, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ChannelToggles } from "@/components/alerts/ChannelToggles";

export function CreateAlertPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [naturalLanguageQuery, setNaturalLanguageQuery] = useState("");
  const [structuredQuery, setStructuredQuery] = useState<AlertQuery | null>(null);
  const [queryExplanation, setQueryExplanation] = useState<string | null>(null);
  const [queryConfidence, setQueryConfidence] = useState<number | null>(null);
  const [querySuggestions, setQuerySuggestions] = useState<string[]>([]);
  const [category, setCategory] = useState<AlertCategory>("CUSTOM");
  const [customCategoryName, setCustomCategoryName] = useState("");
  const [priority, setPriority] = useState<AlertPriority>("P2");
  const [condition, setCondition] = useState<AlertCondition>("the query has any results");
  const [conditionValue, setConditionValue] = useState<number>(0);
  const [channels, setChannels] = useState<AlertChannels>({ email: null, slack: null, teams: null });
  const [testResults, setTestResults] = useState<TestQueryResponse | null>(null);

  const createAlertMutation = useCreateAlert();
  const buildQueryMutation = useBuildQuery();
  const testQueryMutation = useTestQuery();

  const handleCategoryChange = (newCategory: AlertCategory) => {
    setCategory(newCategory);
    if (
      newCategory !== "CUSTOM" &&
      isPredefinedCategory(newCategory) &&
      ALERT_TEMPLATES[newCategory]
    ) {
      const template = ALERT_TEMPLATES[newCategory];
      setNaturalLanguageQuery(template.query);
      setPriority(template.priority);
      // Use functional update to avoid stale closure issues
      setDescription((prevDescription) =>
        template.description && !prevDescription ? template.description : prevDescription
      );
      setCustomCategoryName(""); // Clear custom name when switching to predefined
      // Convert template query to structured query
      handleBuildQuery(template.query);
    } else if (newCategory === "CUSTOM") {
      setCustomCategoryName(""); // Reset custom name
    }
  };

  const handleBuildQuery = useCallback(
    async (queryText?: string) => {
      const textToConvert = queryText || naturalLanguageQuery;
      if (!textToConvert.trim() || textToConvert.length < 10) {
        return;
      }

      try {
        const result = await buildQueryMutation.mutateAsync({
          naturalLanguage: textToConvert,
          includeExamples: true,
        });
        setStructuredQuery(result.query);
        setQueryExplanation(result.explanation);
        setQueryConfidence(result.confidence);
        setQuerySuggestions(result.suggestions);
      } catch (err) {
        console.error("Failed to build query:", err);
        setQuerySuggestions([
          `Error: ${err instanceof Error ? err.message : "Failed to convert query"}`,
        ]);
      }
    },
    [naturalLanguageQuery, buildQueryMutation]
  );

  const handleTestQuery = async () => {
    if (!structuredQuery) {
      return;
    }

    try {
      const result = await testQueryMutation.mutateAsync({
        query: structuredQuery,
        timeWindowHours: 24,
        sampleLimit: 10,
      });
      setTestResults(result);
    } catch (error) {
      console.error("Failed to test query:", error);
      setTestResults({
        total_count: 0,
        sample_traces: [],
        warnings: [`Error: ${error instanceof Error ? error.message : "Failed to test query"}`],
        time_window_start: "",
        time_window_end: "",
        query_valid: false,
        execution_time_ms: 0,
      });
    }
  };

  const handleSubmit = async () => {
    // Require structured query to be already built
    if (!structuredQuery) {
      console.error("No structured query available");
      return;
    }

    const finalCategory =
      category === "CUSTOM" && customCategoryName.trim()
        ? customCategoryName.trim()
        : isPredefinedCategory(category)
          ? category
          : category === "CUSTOM"
            ? "CUSTOM"
            : category;

    // Validate channels - at least one must be set
    if (!channels.email && !channels.slack && !channels.teams) {
      console.error("No notification channel configured");
      return;
    }

    try {
      await createAlertMutation.mutateAsync({
        name,
        description: description || null,
        query: structuredQuery,
        category: finalCategory === "CUSTOM" ? null : (finalCategory as AlertCategory),
        custom_category_name:
          category === "CUSTOM" && customCategoryName.trim() ? customCategoryName.trim() : null,
        priority,
        condition,
        condition_value: condition === "the query has any results" ? null : conditionValue,
        channels,
      });
      navigate("/alerts");
    } catch (err) {
      console.error("Failed to create alert:", err);
    }
  };

  const needsConditionValue = condition !== "the query has any results";

  return (
    <div className="flex flex-1 flex-col gap-6 bg-muted/10 px-4 py-6 lg:px-6 [&_input]:rounded-[10px] [&_textarea]:rounded-[10px]">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" className="rounded-full bg-white" onClick={() => navigate("/alerts")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-xl font-medium">Create Alert</h1>
          <p className="text-sm text-muted-foreground">
            Set up custom alerts based on natural language queries for your AI stack
          </p>
        </div>
      </div>

      <Card className="rounded-[15px] shadow-none">
        <CardHeader>
          <CardTitle>Alert Name</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="alert-name">Alert name</Label>
            <Input
              id="alert-name"
              placeholder="e.g., Critical PII Detection Alert"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-[15px] shadow-none">
        <CardHeader>
          <CardTitle>Category & Priority</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Alert Category</Label>
              <Select
                value={category}
                onValueChange={(value) => handleCategoryChange(value as AlertCategory)}
              >
                <SelectTrigger id="category" className="rounded-full bg-white">
                  <SelectValue>{formatCategoryLabel(category, customCategoryName)}</SelectValue>
                </SelectTrigger>
                <SelectContent className="rounded-[20px] bg-white">
                  {PREDEFINED_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {formatCategoryLabel(cat)}
                    </SelectItem>
                  ))}
                  <SelectItem value="CUSTOM">Custom Category</SelectItem>
                </SelectContent>
              </Select>

              {category === "CUSTOM" && (
                <div className="space-y-2 mt-2">
                  <Label htmlFor="custom-category-name">Custom Category Name</Label>
                  <Input
                    id="custom-category-name"
                    placeholder="e.g., Compliance Audit, Cost Monitoring"
                    value={customCategoryName}
                    onChange={(e) => setCustomCategoryName(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Create your own category name for this alert
                  </p>
                </div>
              )}

              {isPredefinedCategory(category) && (
                <p className="text-xs text-muted-foreground mt-1">
                  {ALERT_TEMPLATES[category].description}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority Level</Label>
              <Select
                value={priority}
                onValueChange={(value) => setPriority(value as AlertPriority)}
              >
                <SelectTrigger id="priority" className="rounded-full bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-[20px] bg-white">
                  {ALERT_PRIORITY_OPTIONS.map((pri) => (
                    <SelectItem key={pri} value={pri}>
                      <div className="flex items-center gap-2">
                        <span>{pri}</span>
                        <span className="text-xs text-muted-foreground">
                          - {PRIORITY_DESCRIPTIONS[pri].label} (SLA:{" "}
                          {PRIORITY_DESCRIPTIONS[pri].sla})
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {PRIORITY_DESCRIPTIONS[priority].label} • SLA: {PRIORITY_DESCRIPTIONS[priority].sla}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-[15px] shadow-none">
        <CardHeader>
          <CardTitle>Query</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Write your alert query in natural language. The system will convert it to a structured
              query.
            </p>
            <Textarea
              placeholder="e.g., Alert me when there are critical PII detection issues from any AI agent"
              value={naturalLanguageQuery}
              onChange={(e) => setNaturalLanguageQuery(e.target.value)}
              onBlur={() => {
                if (naturalLanguageQuery.trim().length >= 10) {
                  handleBuildQuery();
                }
              }}
              className="min-h-[120px] font-mono text-sm"
            />
            <div className="text-xs text-muted-foreground space-y-1">
              <p>Examples:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>"Alert me when the error rate for any AI agent exceeds 5%"</li>
                <li>"Notify me when prompt injection scanner detects high severity issues"</li>
                <li>"Alert when token usage for gpt-4 model exceeds 3x baseline"</li>
              </ul>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleBuildQuery()}
              disabled={
                !naturalLanguageQuery.trim() ||
                naturalLanguageQuery.trim().length < 10 ||
                buildQueryMutation.isPending
              }
            >
              {buildQueryMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Converting...
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Convert Query
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleTestQuery}
              disabled={!structuredQuery || testQueryMutation.isPending}
            >
              {testQueryMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Preview Results
                </>
              )}
            </Button>
          </div>

          {queryExplanation && (
            <div className="mt-4 p-4 bg-muted rounded-md space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-5 w-5 rounded-full bg-green-500 flex items-center justify-center">
                    <svg
                      className="h-3 w-3 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-medium">Query Ready</p>
                </div>
                {queryConfidence !== null && queryConfidence > 0.8 && (
                  <Badge variant="default">
                    {Math.round(queryConfidence * 100)}% confidence
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Your query has been converted successfully. You can preview the
                matching traces below or proceed to create the alert.
              </p>
              {/* Development mode only: show technical details */}
              {import.meta.env.DEV && (
                <>
                  <details className="mt-2">
                    <summary className="text-xs cursor-pointer text-muted-foreground hover:text-foreground">
                      Technical details
                    </summary>
                    <p className="mt-2 text-xs text-muted-foreground">
                      {queryExplanation}
                    </p>
                    {querySuggestions.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium mb-1">Suggestions:</p>
                        <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                          {querySuggestions.map((suggestion, idx) => (
                            <li key={idx}>{suggestion}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </details>
                  {structuredQuery && (
                    <details className="mt-2">
                      <summary className="text-xs cursor-pointer text-muted-foreground hover:text-foreground">
                        View structured query (JSON)
                      </summary>
                      <pre className="mt-2 text-xs bg-background p-2 rounded overflow-auto max-h-40">
                        {JSON.stringify(structuredQuery, null, 2)}
                      </pre>
                    </details>
                  )}
                </>
              )}
            </div>
          )}

          {testResults && (
            <div className="mt-4 p-4 bg-muted rounded-md">
              <p className="text-sm font-medium mb-2">
                Test Results ({testResults.total_count} matches)
              </p>
              {testResults.warnings && testResults.warnings.length > 0 && (
                <div className="mb-2">
                  {testResults.warnings.map((warning: string, idx: number) => (
                    <p key={idx} className="text-xs text-orange-600">
                      {warning}
                    </p>
                  ))}
                </div>
              )}
              {testResults.sample_traces && testResults.sample_traces.length > 0 ? (
                <div className="text-xs text-muted-foreground">
                  <p className="mb-1">Sample traces:</p>
                  <ul className="list-disc list-inside space-y-1">
                    {testResults.sample_traces.slice(0, 5).map((trace) => (
                      <li key={trace.id}>
                        {trace.source} - {trace.event_type} - {trace.severity}
                      </li>
                    ))}
                  </ul>
                  {testResults.sample_traces.length > 5 && (
                    <p className="mt-1">... and {testResults.sample_traces.length - 5} more</p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  No matching traces found in the last 24 hours.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-[15px] shadow-none">
        <CardHeader>
          <CardTitle>Alert Parameters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label htmlFor="condition">Notify me when</Label>
              <Select
                value={condition}
                onValueChange={(value) => setCondition(value as AlertCondition)}
              >
                <SelectTrigger id="condition" className="rounded-full bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-[20px] bg-white">
                  {ALERT_CONDITION_OPTIONS.map((cond) => (
                    <SelectItem key={cond} value={cond}>
                      {cond}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {needsConditionValue && (
            <div className="mt-4 space-y-2">
              <Label htmlFor="condition-value">
                {condition.includes("rows")
                  ? "Threshold (number of rows)"
                  : condition.includes("error rate")
                    ? "Error Rate Threshold (%)"
                    : condition.includes("standard deviations")
                      ? "Standard Deviations"
                      : "Threshold (percentage)"}
              </Label>
              <Input
                id="condition-value"
                type="number"
                min={0}
                step={condition.includes("standard deviations") ? 0.1 : 1}
                value={conditionValue}
                onChange={(e) => setConditionValue(Number(e.target.value))}
                placeholder={
                  condition.includes("rows")
                    ? "10"
                    : condition.includes("error rate")
                      ? "5"
                      : condition.includes("standard deviations")
                        ? "2"
                        : "300"
                }
              />
              {condition.includes("standard deviations") && (
                <p className="text-xs text-muted-foreground">
                  Number of standard deviations from baseline (typically 2-3)
                </p>
              )}
            </div>
          )}

          <p className="text-sm text-muted-foreground mt-4">
            A notification will be sent to your webhooks when{" "}
            <strong>the query returns one or more rows.</strong>
          </p>
        </CardContent>
      </Card>

      <Card className="rounded-[15px] shadow-none">
        <CardHeader>
          <CardTitle>Description</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              This text will be included in notifications. It&apos;s a good place for related links,
              instructions, etc.
            </p>
            <Textarea
              placeholder="Optional description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-[15px] shadow-none">
        <CardHeader>
          <CardTitle>Notification Channels</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Configure where to send notifications when this alert triggers.
          </p>
          <ChannelToggles channels={channels} onChange={setChannels} />
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate("/alerts")}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={
            !name.trim() ||
            !structuredQuery ||
            (!channels.email && !channels.slack && !channels.teams) ||
            createAlertMutation.isPending
          }
        >
          {createAlertMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Creating...
            </>
          ) : (
            "Create Alert"
          )}
        </Button>
      </div>
    </div>
  );
}
